import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, ImageOffIcon, ZoomOutIcon, XIcon, ZapIcon, LightbulbIcon, ToggleLeftIcon, PersonStandingIcon, HeartPulseIcon, Settings2Icon, RadarIcon } from 'lucide-react';
// Define Sensor Types
export type SensorType = 'SEO' | 'SEL' | 'SES' | 'RADAR';
export type ViewMode = 'energy' | 'comfort' | 'airquality' | 'devices';
export interface Sensor {
  id: string;
  type: SensorType;
  label: string;
  roomId: string;
  active: boolean;
  x: number;
  y: number;
}
interface Room {
  id: string;
  name: string;
  temp: number;
  icon: any;
}
interface FloorPlanHeatMapProps {
  rooms: Room[];
  selectedRoomId: string;
  onSelectRoom: (id: string) => void;
  isZoomed?: boolean;
  onZoomOut?: () => void;
  onSelectSensor?: (sensor: Sensor) => void;
  selectedSensorId?: string | null;
  viewMode?: ViewMode;
  seniorCareEnabled?: boolean;
  onToggleSeniorCare?: () => void;
  onOpenEditor?: () => void;
  zoneOverrides?: {
    id: string;
    top: number;
    left: number;
    width: number;
    height: number;
  }[];
}
// Mock room data for different view modes
const ROOM_ENERGY: Record<string, number> = {
  living: 12.4,
  bedroom: 5.8,
  kitchen: 8.2,
  bathroom: 2.1,
  bedroom2: 4.3,
  master_bedroom: 6.7,
  bathroom2: 1.8,
  dressroom: 1.2,
  outdoor_unit: 3.5,
  entrance: 0.8,
  alpha_room: 2.9
};
const ROOM_COMFORT: Record<string, number> = {
  living: 92,
  bedroom: 78,
  kitchen: 65,
  bathroom: 85,
  bedroom2: 82,
  master_bedroom: 74,
  bathroom2: 88,
  dressroom: 70,
  outdoor_unit: 45,
  entrance: 60,
  alpha_room: 76
};
// Mock Sensor Data
export const MOCK_SENSORS: Record<string, Sensor[]> = {
  living: [{
    id: 'seo-living-1',
    type: 'SEO',
    label: 'SEO-01',
    roomId: 'living',
    active: true,
    x: 30,
    y: 40
  }, {
    id: 'sel-living-1',
    type: 'SEL',
    label: 'SEL-01',
    roomId: 'living',
    active: true,
    x: 60,
    y: 25
  }, {
    id: 'ses-living-1',
    type: 'SES',
    label: 'SES-01',
    roomId: 'living',
    active: false,
    x: 85,
    y: 70
  }],
  bedroom: [{
    id: 'seo-bedroom-1',
    type: 'SEO',
    label: 'SEO-02',
    roomId: 'bedroom',
    active: true,
    x: 25,
    y: 50
  }, {
    id: 'sel-bedroom-1',
    type: 'SEL',
    label: 'SEL-02',
    roomId: 'bedroom',
    active: true,
    x: 50,
    y: 30
  }, {
    id: 'ses-bedroom-1',
    type: 'SES',
    label: 'SES-02',
    roomId: 'bedroom',
    active: true,
    x: 80,
    y: 60
  }, {
    id: 'radar-bedroom-1',
    type: 'RADAR',
    label: 'radar-1',
    roomId: 'bedroom',
    active: true,
    x: 50,
    y: 80
  }],
  kitchen: [{
    id: 'seo-kitchen-1',
    type: 'SEO',
    label: 'SEO-03',
    roomId: 'kitchen',
    active: true,
    x: 35,
    y: 45
  }, {
    id: 'sel-kitchen-1',
    type: 'SEL',
    label: 'SEL-03',
    roomId: 'kitchen',
    active: false,
    x: 65,
    y: 30
  }, {
    id: 'ses-kitchen-1',
    type: 'SES',
    label: 'SES-03',
    roomId: 'kitchen',
    active: true,
    x: 20,
    y: 70
  }],
  bathroom: [{
    id: 'seo-bathroom-1',
    type: 'SEO',
    label: 'SEO-04',
    roomId: 'bathroom',
    active: true,
    x: 40,
    y: 40
  }, {
    id: 'sel-bathroom-1',
    type: 'SEL',
    label: 'SEL-04',
    roomId: 'bathroom',
    active: true,
    x: 60,
    y: 60
  }],
  bedroom2: [{
    id: 'sel-bedroom2-1',
    type: 'SEL',
    label: 'SEL-05',
    roomId: 'bedroom2',
    active: true,
    x: 50,
    y: 50
  }],
  master_bedroom: [{
    id: 'seo-master-1',
    type: 'SEO',
    label: 'SEO-05',
    roomId: 'master_bedroom',
    active: true,
    x: 50,
    y: 50
  }],
  bathroom2: [{
    id: 'ses-bathroom2-1',
    type: 'SES',
    label: 'SES-04',
    roomId: 'bathroom2',
    active: true,
    x: 50,
    y: 50
  }],
  dressroom: [{
    id: 'sel-dressroom-1',
    type: 'SEL',
    label: 'SEL-06',
    roomId: 'dressroom',
    active: true,
    x: 50,
    y: 50
  }],
  outdoor_unit: [{
    id: 'seo-outdoor-1',
    type: 'SEO',
    label: 'SEO-06',
    roomId: 'outdoor_unit',
    active: true,
    x: 50,
    y: 50
  }],
  entrance: [{
    id: 'ses-entrance-1',
    type: 'SES',
    label: 'SES-05',
    roomId: 'entrance',
    active: true,
    x: 50,
    y: 50
  }],
  alpha_room: [{
    id: 'sel-alpha-1',
    type: 'SEL',
    label: 'SEL-07',
    roomId: 'alpha_room',
    active: false,
    x: 50,
    y: 50
  }]
};
const ROOM_COORDINATES: Record<string, {
  top: string;
  left: string;
  width: string;
  height: string;
}> = {
  living: {
    top: '48.1%',
    left: '34.6%',
    width: '35.9%',
    height: '45.3%'
  },
  bedroom: {
    top: '55.3%',
    left: '5.8%',
    width: '27.5%',
    height: '25.1%'
  },
  kitchen: {
    top: '14.5%',
    left: '38.6%',
    width: '22.7%',
    height: '29.9%'
  },
  bathroom: {
    top: '46.2%',
    left: '80.7%',
    width: '15.8%',
    height: '9.6%'
  },
  bedroom2: {
    top: '28.8%',
    left: '5.8%',
    width: '11.9%',
    height: '14.7%'
  },
  master_bedroom: {
    top: '57.4%',
    left: '72%',
    width: '24.7%',
    height: '31.3%'
  },
  bathroom2: {
    top: '28%',
    left: '5%',
    width: '12%',
    height: '15%'
  },
  dressroom: {
    top: '44.5%',
    left: '5.9%',
    width: '19.5%',
    height: '8.9%'
  },
  outdoor_unit: {
    top: '11.2%',
    left: '6.1%',
    width: '11.3%',
    height: '16.8%'
  },
  entrance: {
    top: '34.5%',
    left: '67.7%',
    width: '12.8%',
    height: '22.2%'
  },
  alpha_room: {
    top: '8.6%',
    left: '36.4%',
    width: '11.6%',
    height: '11.7%'
  }
};
const DEFAULT_COORDS = {
  top: '40%',
  left: '40%',
  width: '20%',
  height: '20%'
};
const parsePercent = (val: string) => parseFloat(val.replace('%', ''));
const FLOOR_PLAN_URL = `https://iot.seqsys.net/api/images/public/VhhX6m93VQ03Pw5HhZC5oAOQcjFABApg`;
// Rooms that have SES (occupancy) sensors
const ROOMS_WITH_SES = Object.entries(MOCK_SENSORS).filter(([_, sensors]) => sensors.some(s => s.type === 'SES')).map(([roomId]) => roomId);
export function FloorPlanHeatMap({
  rooms,
  selectedRoomId,
  onSelectRoom,
  isZoomed = false,
  onZoomOut,
  onSelectSensor,
  selectedSensorId,
  viewMode = 'energy',
  seniorCareEnabled = false,
  onToggleSeniorCare,
  onOpenEditor,
  zoneOverrides
}: FloorPlanHeatMapProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  // Build effective coordinates: merge static defaults with any saved overrides
  const effectiveCoords: Record<string, {
    top: string;
    left: string;
    width: string;
    height: string;
  }> = useMemo(() => {
    const base = {
      ...ROOM_COORDINATES
    };
    if (zoneOverrides) {
      zoneOverrides.forEach(z => {
        base[z.id] = {
          top: `${z.top}%`,
          left: `${z.left}%`,
          width: `${z.width}%`,
          height: `${z.height}%`
        };
      });
    }
    return base;
  }, [zoneOverrides]);
  const getSensorIcon = (type: SensorType) => {
    switch (type) {
      case 'SEO':
        return ZapIcon;
      case 'SEL':
        return LightbulbIcon;
      case 'SES':
        return ToggleLeftIcon;
      case 'RADAR':
        return RadarIcon;
      default:
        return ZapIcon;
    }
  };
  const getSensorColor = (type: SensorType) => {
    switch (type) {
      case 'SEO':
        return 'text-violet-400 bg-violet-500/20 border-violet-400';
      case 'SEL':
        return 'text-amber-400 bg-amber-500/20 border-amber-400';
      case 'SES':
        return 'text-cyan-400 bg-cyan-500/20 border-cyan-400';
      case 'RADAR':
        return 'text-rose-400 bg-rose-500/20 border-rose-400';
      default:
        return 'text-white bg-white/20 border-white';
    }
  };
  // Color schemes per view mode
  const getTempColor = (temp: number) => {
    if (temp < 22) return {
      bg: 'bg-cyan-500/30',
      border: 'border-cyan-400/50',
      text: 'text-cyan-200'
    };
    if (temp < 24) return {
      bg: 'bg-emerald-500/30',
      border: 'border-emerald-400/50',
      text: 'text-emerald-200'
    };
    if (temp < 26) return {
      bg: 'bg-amber-500/30',
      border: 'border-amber-400/50',
      text: 'text-amber-200'
    };
    return {
      bg: 'bg-red-500/30',
      border: 'border-red-400/50',
      text: 'text-red-200'
    };
  };
  const getEnergyColor = (kwh: number) => {
    if (kwh < 4) return {
      bg: 'bg-emerald-500/30',
      border: 'border-emerald-400/50',
      text: 'text-emerald-200'
    };
    if (kwh < 8) return {
      bg: 'bg-cyan-500/30',
      border: 'border-cyan-400/50',
      text: 'text-cyan-200'
    };
    if (kwh < 12) return {
      bg: 'bg-amber-500/30',
      border: 'border-amber-400/50',
      text: 'text-amber-200'
    };
    return {
      bg: 'bg-red-500/30',
      border: 'border-red-400/50',
      text: 'text-red-200'
    };
  };
  const getComfortColor = (score: number) => {
    if (score >= 85) return {
      bg: 'bg-emerald-500/30',
      border: 'border-emerald-400/50',
      text: 'text-emerald-200'
    };
    if (score >= 70) return {
      bg: 'bg-cyan-500/30',
      border: 'border-cyan-400/50',
      text: 'text-cyan-200'
    };
    if (score >= 50) return {
      bg: 'bg-amber-500/30',
      border: 'border-amber-400/50',
      text: 'text-amber-200'
    };
    return {
      bg: 'bg-red-500/30',
      border: 'border-red-400/50',
      text: 'text-red-200'
    };
  };
  const getRoomColors = (room: Room) => {
    switch (viewMode) {
      case 'energy':
        return getEnergyColor(ROOM_ENERGY[room.id] || 0);
      case 'comfort':
        return getComfortColor(ROOM_COMFORT[room.id] || 0);
      case 'airquality':
        return getComfortColor(ROOM_COMFORT[room.id] || 0);
      default:
        return getEnergyColor(ROOM_ENERGY[room.id] || 0);
    }
  };
  const getRoomDisplayValue = (room: Room) => {
    switch (viewMode) {
      case 'energy':
        return `${ROOM_ENERGY[room.id] || 0} kWh`;
      case 'comfort':
        return `${ROOM_COMFORT[room.id] || 0}점`;
      case 'airquality':
        return `${ROOM_COMFORT[room.id] || 0}점`;
      default:
        return `${ROOM_ENERGY[room.id] || 0} kWh`;
    }
  };
  const getLegendItems = () => {
    switch (viewMode) {
      case 'energy':
        return [{
          color: 'bg-emerald-500/60',
          label: '절약'
        }, {
          color: 'bg-cyan-500/60',
          label: '보통'
        }, {
          color: 'bg-amber-500/60',
          label: '높음'
        }, {
          color: 'bg-red-500/60',
          label: '과다'
        }];
      case 'comfort':
        return [{
          color: 'bg-emerald-500/60',
          label: '쾌적'
        }, {
          color: 'bg-cyan-500/60',
          label: '양호'
        }, {
          color: 'bg-amber-500/60',
          label: '보통'
        }, {
          color: 'bg-red-500/60',
          label: '불쾌'
        }];
      case 'airquality':
        return [{
          color: 'bg-emerald-500/60',
          label: '좋음'
        }, {
          color: 'bg-cyan-500/60',
          label: '보통'
        }, {
          color: 'bg-amber-500/60',
          label: '나쁨'
        }, {
          color: 'bg-red-500/60',
          label: '매우나쁨'
        }];
      default:
        return [];
    }
  };
  const getLegendTitle = () => {
    switch (viewMode) {
      case 'energy':
        return '에너지';
      case 'comfort':
        return '쾌적성';
      case 'airquality':
        return '공기질';
      default:
        return '';
    }
  };
  const getZoomTransform = () => {
    if (!isZoomed || !selectedRoomId) return {
      x: 0,
      y: 0,
      scale: 1
    };
    const coords = effectiveCoords[selectedRoomId] || DEFAULT_COORDS;
    const top = parsePercent(coords.top);
    const left = parsePercent(coords.left);
    const width = parsePercent(coords.width);
    const height = parsePercent(coords.height);
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    const scale = 2.2;
    const x = (50 - centerX) * scale;
    const y = (50 - centerY) * scale;
    return {
      x: `${x}%`,
      y: `${y}%`,
      scale
    };
  };
  const zoomState = getZoomTransform();
  return <div className="relative w-full flex items-center justify-center p-2 overflow-hidden h-full pt-[0px] pb-[0px] pl-[0px] pr-[0px]" data-id="element-210">
      {/* Edit Button - Top Left */}
      {!isZoomed && onOpenEditor && <div className="absolute top-4 left-4 z-50" data-id="element-211">
          <button onClick={onOpenEditor} className="flex items-center gap-2 bg-black/60 backdrop-blur-md rounded-full px-3 py-1.5 border border-white/10 shadow-lg text-slate-400 hover:text-cyan-400 hover:border-cyan-400/30 transition-all" data-id="element-212">
            <Settings2Icon className="w-3.5 h-3.5" data-id="element-213" />
            <span className="text-[0.85rem] font-bold uppercase tracking-wider" data-id="element-214">
              편집
            </span>
          </button>
        </div>}

      {/* Senior Care Toggle - Top Right */}
      {!isZoomed && <div className="absolute top-4 right-4 z-50" data-id="element-215">
          <div className="flex items-center gap-2 bg-black/60 backdrop-blur-md rounded-full px-3 py-1.5 border border-white/10 shadow-lg" data-id="element-216">
            <HeartPulseIcon className={`w-3.5 h-3.5 transition-colors ${seniorCareEnabled ? 'text-red-400' : 'text-slate-500'}`} data-id="element-217" />
            <span className={`text-[0.9rem] font-bold uppercase tracking-wider transition-colors ${seniorCareEnabled ? 'text-red-300' : 'text-slate-500'}`} data-id="element-218">
              시니어케어
            </span>
            <button onClick={onToggleSeniorCare} className={`relative w-8 h-4.5 rounded-full transition-colors duration-300 focus:outline-none ${seniorCareEnabled ? 'bg-red-500' : 'bg-slate-700'}`} style={{
          width: '2.1rem',
          height: '1.25rem'
        }} data-id="element-219">
              <div className={`absolute top-0.5 left-0.5 w-3.5 h-3.5 rounded-full bg-white shadow-sm transition-transform duration-300 ${seniorCareEnabled ? 'translate-x-3.5' : 'translate-x-0'}`} style={{
            width: '1rem',
            height: '1rem',
            transform: seniorCareEnabled ? 'translateX(0.82rem)' : 'translateX(0)'
          }} data-id="element-220" />
            </button>
          </div>
        </div>}

      {/* Zoom Out Button Overlay */}
      <AnimatePresence data-id="element-221">
        {isZoomed && onZoomOut && <motion.button initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.8
      }} onClick={e => {
        e.stopPropagation();
        onZoomOut();
      }} className="absolute top-4 left-4 z-50 p-2 bg-black/60 backdrop-blur-md border border-white/10 rounded-full text-white hover:bg-black/80 transition-colors shadow-lg group" data-id="element-222">
            <div className="flex items-center gap-2 px-2" data-id="element-223">
              <ZoomOutIcon className="w-4 h-4" data-id="element-224" />
              <span className="text-xs font-medium pr-1" data-id="element-225">돌아가기</span>
            </div>
          </motion.button>}
      </AnimatePresence>

      <motion.div className="relative w-full transition-transform duration-700 ease-in-out" style={{
      maxWidth: 1600,
      transformOrigin: 'center center'
    }} animate={{
      scale: zoomState.scale,
      x: zoomState.x,
      y: zoomState.y
    }} transition={{
      type: 'spring',
      stiffness: 200,
      damping: 25
    }} data-id="element-226">
        <img src={FLOOR_PLAN_URL} alt="Floor Plan" onLoad={() => setImageLoaded(true)} onError={() => setImageError(true)} className={`w-full h-auto block opacity-80 drop-shadow-2xl transition-opacity duration-500 ${imageLoaded ? 'opacity-80' : 'opacity-0'}`} crossOrigin="anonymous" data-id="element-227" />

        {!imageLoaded && !imageError && <div className="w-full flex items-center justify-center py-32" data-id="element-228">
            <div className="flex flex-col items-center gap-3 text-slate-500" data-id="element-229">
              <div className="w-8 h-8 border-2 border-cyan-400/50 border-t-cyan-400 rounded-full animate-spin" data-id="element-230" />
              <span className="text-xs font-mono uppercase tracking-wider" data-id="element-231">
                평면도 로딩 중...
              </span>
            </div>
          </div>}

        {imageError && <div className="w-full flex items-center justify-center py-32" data-id="element-232">
            <div className="flex flex-col items-center gap-3 text-slate-500" data-id="element-233">
              <ImageOffIcon className="w-10 h-10 text-slate-600" data-id="element-234" />
              <span className="text-xs font-mono uppercase tracking-wider" data-id="element-235">
                평면도가 없습니다.
              </span>
            </div>
          </div>}

        {/* Connected Devices overlay - only in devices mode */}
        {imageLoaded && viewMode === 'devices' && <div className="absolute inset-0 z-30" data-id="element-236">
            {/* 스마트글라스 - 거실 하단 창문 (bottom center) */}
            <motion.button initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} onClick={e => {
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent('device-toast', {
            detail: {
              name: '스마트글라스'
            }
          }));
        }} className="absolute rounded-md border-2 border-cyan-400 bg-cyan-900/90 flex items-center justify-center cursor-pointer hover:bg-cyan-800 hover:border-cyan-300 transition-all shadow-lg" style={{
          top: '92.5%',
          left: '37%',
          width: '32%',
          height: '4.5%'
        }} data-id="element-237">
              <span className="text-[0.7rem] font-extrabold text-cyan-200 uppercase tracking-wider" data-id="element-238">
                스마트글라스
              </span>
            </motion.button>

            {/* 전동블라인드 - 안방 우측 창문 (right edge) */}
            <motion.button initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3
        }} onClick={e => {
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent('device-toast', {
            detail: {
              name: '전동블라인드'
            }
          }));
        }} className="absolute rounded-md border-2 border-amber-400 bg-amber-900/90 flex items-center justify-center cursor-pointer hover:bg-amber-800 hover:border-amber-300 transition-all shadow-lg" style={{
          top: '70%',
          left: '95.5%',
          width: '4%',
          height: '20%'
        }} data-id="element-239">
              <span className="text-[0.55rem] font-extrabold text-amber-200 uppercase tracking-wider [writing-mode:vertical-rl]" data-id="element-240">
                블라인드
              </span>
            </motion.button>

            {/* 전동커튼 - 침실2 좌측 창문 (left edge) */}
            <motion.button initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.4
        }} onClick={e => {
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent('device-toast', {
            detail: {
              name: '전동커튼'
            }
          }));
        }} className="absolute rounded-md border-2 border-amber-400 bg-amber-900/90 flex items-center justify-center cursor-pointer hover:bg-amber-800 hover:border-amber-300 transition-all shadow-lg" style={{
          top: '42%',
          left: '0.5%',
          width: '4%',
          height: '14%'
        }} data-id="element-241">
              <span className="text-[0.55rem] font-extrabold text-amber-200 uppercase tracking-wider [writing-mode:vertical-rl]" data-id="element-242">
                커튼
              </span>
            </motion.button>

            {/* 전열교환기 - 실외기실 (top left) */}
            <motion.button initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.5
        }} onClick={e => {
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent('device-toast', {
            detail: {
              name: '전열교환기'
            }
          }));
        }} className="absolute rounded-lg border-2 border-emerald-400 bg-emerald-900/90 flex flex-col items-center justify-center cursor-pointer hover:bg-emerald-800 hover:border-emerald-300 transition-all shadow-lg" style={{
          top: '11%',
          left: '6%',
          width: '12%',
          height: '17%'
        }} data-id="element-243">
              <span className="text-[0.7rem] font-extrabold text-emerald-200 uppercase tracking-wider" data-id="element-244">
                전열교환기
              </span>
            </motion.button>
          </div>}

        {imageLoaded && viewMode !== 'devices' && <div className="absolute inset-0" data-id="element-245">
            {rooms.map(room => {
          const coords = effectiveCoords[room.id] || DEFAULT_COORDS;
          const isSelected = selectedRoomId === room.id;
          const colors = getRoomColors(room);
          const isDimmed = isZoomed && !isSelected;
          return <motion.button key={`${room.id}-${viewMode}`} onClick={e => {
            if (!isZoomed) onSelectRoom(room.id);
          }} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: isDimmed ? 0.3 : 1,
            scale: isSelected ? 1.03 : 1,
            zIndex: isSelected ? 20 : 10
          }} whileHover={{
            scale: isDimmed ? 1 : 1.02,
            zIndex: 15,
            opacity: isDimmed ? 0.8 : 1
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 25
          }} style={{
            top: coords.top,
            left: coords.left,
            width: coords.width,
            height: coords.height
          }} className={`
                    absolute rounded-lg border-2 backdrop-blur-[2px] flex flex-col items-center justify-center group cursor-pointer
                    ${colors.bg}
                    ${isSelected ? 'border-white shadow-[0_0_20px_rgba(255,255,255,0.3)] ring-2 ring-cyan-400/50' : `${colors.border}`}
                    ${isZoomed && isSelected ? '!border-transparent !bg-transparent !shadow-none !backdrop-blur-none' : ''}
                  `} data-id="element-246">
                  {(!isZoomed || !isSelected) && <>
                      {isSelected && <span className="absolute inset-0 rounded-lg animate-pulse bg-white/10" data-id="element-247" />}
                      <div className="relative z-10 flex flex-col items-center text-center transition-all duration-300" data-id="element-248">
                        <span className={`text-[0.6rem] font-bold uppercase tracking-wider mb-0.5 ${isSelected ? 'text-white' : 'text-white/70'}`} data-id="element-249">
                          {room.name}
                        </span>
                        <span className={`text-[0.7rem] font-mono font-bold ${colors.text} drop-shadow-md`} data-id="element-250">
                          {getRoomDisplayValue(room)}
                        </span>
                      </div>
                      <div className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ${isSelected ? 'bg-white shadow-[0_0_8px_white]' : colors.text.replace('text-', 'bg-')}`} data-id="element-251" />

                      {/* Senior Care Icon - centered inside room button */}
                      {seniorCareEnabled && ROOMS_WITH_SES.includes(room.id) && <motion.div initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} exit={{
                scale: 0,
                opacity: 0
              }} className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none" data-id="element-252">
                            <motion.div animate={{
                  opacity: [0.4, 1, 0.4],
                  scale: [0.9, 1.1, 0.9]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="relative flex items-center justify-center" data-id="element-253">
                              <div className="absolute w-10 h-10 rounded-full bg-red-500/20 animate-ping" style={{
                    animationDuration: '2.5s'
                  }} data-id="element-254" />
                              <div className="relative w-8 h-8 rounded-full bg-red-500/30 border-2 border-red-400/60 flex items-center justify-center shadow-[0_0_15px_rgba(239,68,68,0.5)]" data-id="element-255">
                                <PersonStandingIcon className="w-5 h-5 text-red-400" data-id="element-256" />
                              </div>
                            </motion.div>
                          </motion.div>}
                    </>}

                  {isZoomed && isSelected && MOCK_SENSORS[room.id]?.map(sensor => {
              const Icon = getSensorIcon(sensor.type);
              const colorClasses = getSensorColor(sensor.type);
              const isSensorSelected = selectedSensorId === sensor.id;
              return <motion.div key={sensor.id} initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                delay: 0.3,
                type: 'spring'
              }} className="absolute" style={{
                left: `${sensor.x}%`,
                top: `${sensor.y}%`,
                transform: 'translate(-50%, -50%)'
              }} onClick={e => {
                e.stopPropagation();
                onSelectSensor?.(sensor);
              }} data-id="element-257">
                          {/* SEL active: slow spreading light glow */}
                          {sensor.type === 'SEL' && sensor.active && <>
                              <motion.span className="absolute inset-0 rounded-full bg-amber-400/40" style={{
                    transformOrigin: 'center'
                  }} animate={{
                    scale: [1, 3.5],
                    opacity: [0.5, 0]
                  }} transition={{
                    duration: 2.8,
                    repeat: Infinity,
                    ease: 'easeOut',
                    repeatDelay: 0.4
                  }} data-id="element-258" />
                              <motion.span className="absolute inset-0 rounded-full bg-amber-300/25" style={{
                    transformOrigin: 'center'
                  }} animate={{
                    scale: [1, 2.5],
                    opacity: [0.4, 0]
                  }} transition={{
                    duration: 2.8,
                    repeat: Infinity,
                    ease: 'easeOut',
                    delay: 1.0,
                    repeatDelay: 0.4
                  }} data-id="element-259" />
                            </>}

                          <div className={`relative z-10 w-4 h-4 rounded-full flex items-center justify-center border-2 shadow-lg transition-all duration-300 ${colorClasses} ${isSensorSelected ? 'scale-125 ring-2 ring-white shadow-[0_0_15px_rgba(255,255,255,0.5)]' : 'hover:scale-110'}`} data-id="element-260">
                            <Icon className="w-2 h-2" data-id="element-261" />
                          </div>
                          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm rounded text-[0.5rem] font-mono text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-262">
                            {sensor.label}
                          </div>
                        </motion.div>;
            })}
                </motion.button>;
        })}
          </div>}
      </motion.div>

      {/* Legend - moved to outer container bottom */}
      {imageLoaded && !isZoomed && viewMode !== 'devices' && <div className="text-[0.9rem] absolute bottom-3 left-3 right-3 z-40 bg-black/50 backdrop-blur-md px-4 py-2 rounded-lg border border-white/10 text-[10px] text-slate-400 hidden sm:flex items-center justify-center gap-3" data-id="element-263">
          <MapPinIcon className="w-3 h-3 text-cyan-400 flex-shrink-0" data-id="element-264" />
          <span className="text-[0.8rem] uppercase tracking-wider font-bold" data-id="element-265">
            {getLegendTitle()}
          </span>
          <span className="text-slate-500" data-id="element-266">|</span>
          <div className="flex items-center gap-3" data-id="element-267">
            {getLegendItems().map(item => <span key={item.label} className="text-[0.8rem] flex items-center gap-1" data-id="element-268">
                <span className={`w-2 h-2 rounded-full ${item.color}`} data-id="element-269" />
                {item.label}
              </span>)}
          </div>
        </div>}
    </div>;
}